import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import {
  performUserLoginActions,
  setUserData as setUserRemote,
} from '@/services/userService'
import { sendUserEvent, getConfig } from '../helpers/cerebro'
import { useProductStore } from '@/store/products'
import * as Sentry from '@sentry/vue'

export const useUserStore = defineStore('user', () => {
  const user = ref()
  const userData = ref()
  const userConfig = ref({})
  const cerebroId = ref()
  const redirectLink = ref()
  const trackerName = ref()
  const productStore = useProductStore()
  const userAnswers = ref({})
  const emailMismatch = ref(false)

  const setUserData = data => {
    userData.value = data
  }

  const setUser = async (authUser, isJustRefetch, source) => {
    if (authUser) {
      const dataToSet = {
        ...userData.value,
        userId: authUser.uid,
        firebaseUserId: authUser.uid,
        email: authUser.email,
        isEmailVerified: authUser.emailVerified,
        createdAt: authUser.metadata.createdAt,
        providerData: authUser.providerData,
      }
      const userDataPayload = await performUserLoginActions(
        authUser,
        dataToSet,
        isJustRefetch,
        source,
      )
      setUserData({ ...dataToSet, ...userDataPayload })
      user.value = authUser
      await getConfig(authUser.uid)
      await productStore.refetchProducts('default')
    } else {
      setUserData({})
      sendUserEvent()
      Sentry.setUser(null)
      user.value = null
    }
  }

  const editUserData = newUserData => {
    for (let key in newUserData) {
      userData.value[key] = newUserData[key]
    }
  }

  const updateFirebaseUserData = async () => {
    await setUserRemote(userData.value)
  }

  const getCurrentUser = () => {
    return new Promise((resolve, _) => {
      if (user.value === undefined) {
        const removeListener = watch(user, value => {
          removeListener()
          resolve(value)
        })
      } else {
        resolve(user.value)
      }
    })
  }

  const isUserPro = () => {
    return (
      !!userData.value &&
      !!userData.value.subscriptions &&
      userData.value.subscriptions.isActive != null &&
      userData.value.subscriptions.isActive
    )
  }

  const isSubscribedRecurring = () => {
    return (
      isUserPro() &&
      userData.value.subscriptions.interval != null &&
      userData.value.subscriptions.interval !== 'lifetime'
    )
  }

  const getSubscription = () => {
    if (isUserPro()) {
      return userData.value.subscriptions
    }
    return null
  }

  const setConfig = data => {
    userConfig.value = data
  }

  const getParameter = key => {
    if (userConfig.value && userConfig.value[key]) {
      return userConfig.value[key]
    }
  }

  const getIsSubsPaddle = () => {
    return userData.value.subscriptions?.paymentProcessor === 'paddle'
  }

  const setCerebroId = cid => {
    cerebroId.value = cid
  }

  const setRedirectLink = link => {
    redirectLink.value = link
  }

  const getRedirectLink = () => {
    return redirectLink.value
  }

  const setTrackerName = link => {
    trackerName.value = link
  }

  const getTrackerName = () => {
    return trackerName.value
  }

  const setAnswers = answers => {
    userAnswers.value = answers
  }

  const getAnswers = () => {
    return userAnswers.value
  }

  const getEmailMismatch = () => {
    return emailMismatch.value
  }

  const setEmailMismatch = value => {
    emailMismatch.value = value
  }

  return {
    user,
    userData,
    cerebroId,
    userAnswers,
    setUser,
    getCurrentUser,
    editUserData,
    updateFirebaseUserData,
    isUserPro,
    isSubscribedRecurring,
    getSubscription,
    getParameter,
    setConfig,
    getIsSubsPaddle,
    setCerebroId,
    getTrackerName,
    setTrackerName,
    getRedirectLink,
    setRedirectLink,
    getAnswers,
    setAnswers,
    getEmailMismatch,
    setEmailMismatch,
  }
})
